import {useContext, useEffect, useRef, useState} from 'react'

import PropTypes from 'prop-types'

import jsCookie from '@s-ui/js/lib/cookie'
import AtomButton, {atomButtonDesigns, atomButtonShapes, atomButtonSizes} from '@s-ui/react-atom-button'
import Context from '@s-ui/react-context'
import Helmet from '@s-ui/react-head'
import {useMount, useOnScreen} from '@s-ui/react-hooks'
import {useRouter} from '@s-ui/react-router'
import ScriptJsonLD from '@s-ui/react-script-json-ld'
import ActionSaveNews from '@adv-mt/mt-actions-save-news'
import AdDisplay from '@adv-mt/mt-ad-display'
import BannerBasic from '@adv-mt/mt-banner-basic'
import ButtonSticky from '@adv-mt/mt-button-sticky'
import FeedArticles from '@adv-mt/mt-feed-articles'
import {GALLERY_ASPECT_RATIO} from '@adv-mt/mt-gallery-basic'
import {
  imageUrlAdapterAspectRatios,
  imageUrlAdapterImageTypes,
  imageUrlAdapterTransformTypes,
  useImageUrlAdapter
} from '@adv-mt/mt-hook-use-image-url-adapter'
import Layout from '@adv-mt/mt-layout-basic'
import {useMediaQuery, useMediaQueryBreakpoints} from '@adv-mt/mt-layout-render-media-query'
import MotorTagCloud from '@adv-mt/mt-list-tagcloud'
import Breadcrumb from '@adv-mt/mt-navigation-breadcrumb'
import OnboardingCoachmark from '@adv-mt/mt-onboarding-coachmark'
import PanelFaqs from '@adv-mt/mt-panel-faqs'
import PanelNewsDetailHeader from '@adv-mt/mt-panel-news-detail-header'
import SectionBasic from '@adv-mt/mt-section-basic'
import SharedSmartbanner, {sharedSmartbannerPositions} from '@adv-mt/mt-shared-smartbanner'
import TitleBasic, {titleBasicSizes, titleBasicTags} from '@adv-mt/mt-title-basic/src'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import LinkFactory from '../../../components/LinkFactory/index.js'
import {ArticleBody} from './components/ArticleBody.js'
import {CommentsAndReviewsSection} from './components/CommentsAndReviewsSection.js'
import ImageGallerySection from './components/ImageGallerySection.js'
import {ProsAndConsSection} from './components/ProsAndConsSection.js'
import {RelatedAdsSection} from './components/RelatedAdsSection.js'
import {Sidebar} from './components/Sidebar/index.js'
import {NEWS_DETAIL_BASE_CLASS, NEWS_DETAIL_EXTRA_INFO_BASE_CLASS} from './config.js'
import {getInitialProps} from './getInitialProps.js'
import {Skeleton} from './Skeleton.js'
import {transformToKebabCase} from './utils.js'

// TODO: update size based on device https://jira.ets.mpi-internal.com/browse/MTR-70175
const DEFAULT_IMAGE_URL = ''

const scrollParams = {block: 'center', behavior: 'smooth'}

const NewsDetail = ({
  __HTTP__ = {},
  adsNew,
  adsRenting,
  allowComments,
  articleId,
  articleSlug,
  author,
  bestOffers = [],
  body,
  breadcrumbs = [],
  category,
  comments,
  customContentConfig,
  date,
  faqsForUrlItems = [],
  hasSeenSaveNewsCoachmark = false,
  helmet,
  images = [],
  isCurrentArticleSaved = false,
  isNewsBookmarksEnabled = false,
  isNewsCommentsPage,
  loadThirdParties,
  make,
  model,
  origin,
  pageName,
  professionalSellerAds = [],
  professionalSellerAdsListingUrl = '',
  prosAndCons,
  rating, // TODO: to be used by component https://jira.ets.mpi-internal.com/browse/MTR-62557
  relatedAds = [],
  relatedContent,
  savedArticleIds = [],
  showConfigurator,
  tag,
  tagClouds,
  title,
  totalNumberOfComments,
  vehicles
}) => {
  const {i18n, domain} = useContext(Context)
  const {push} = useRouter()
  const {page: analyticsTrackPage} = useAnalyticsTrack()
  const getImagesUrlsAdapted = useImageUrlAdapter()
  const [isSidebarOnScreen, setIsSidebarOnScreen] = useState(false)
  const headerRef = useRef()
  const [isDescriptionOnScreen] = useOnScreen({
    ref: headerRef,
    once: false
  })
  const {redirectTo} = __HTTP__

  const domainConfig = domain.get('config')
  const {NEWS_COMMENTS_PAGE_NAME, VEHICLE_CONFIGURATOR_PATH} = domainConfig.get('page')
  const {
    ARTICLE_BODY_IMAGE_WIDTH,
    NEWSLETTER_BODY_ENABLED,
    NEWSLETTER_SIDEBAR_ENABLED = false
  } = domainConfig.get('editorial')
  const isWebview = domainConfig.get('isWebview')

  const {SMARTBANNER_SOURCE_IDS} = domainConfig.get('smartbanner')

  const {
    HIT_INFORMATIONS: {SMARTBANNER_POSITION_TOP: hitInformationSmartbannerPositionTop},
    HIT_ORIGINS: {SMARTBANNER_NEWS_DETAIL: hitOriginSmartbannerNewsDetail},
    TRACKING_DATA_BY_PAGE: {
      NEWS_DETAIL: {page_type: newsDetailPageType}
    }
  } = domainConfig.get('tracking')

  const savedIdsSet = new Set(savedArticleIds)

  const [mainImage = DEFAULT_IMAGE_URL] = images

  const hasBestOffers = Boolean(bestOffers.length)
  const hasRelatedContent = Boolean(relatedContent.length)
  const shouldRenderRelatedContent = hasRelatedContent && !isWebview && hasBestOffers
  const shouldRenderRelatedContentAtSidebar = hasRelatedContent && !isWebview && !hasBestOffers

  const hasBodyNewsletter = NEWSLETTER_BODY_ENABLED && !isWebview

  const isMobile = domainConfig.get('isMobile')
  const initialMediaQueryMatch = {
    defaultMatches: !isMobile
  }
  const isTabletOrHigher = useMediaQuery(useMediaQueryBreakpoints.sm, initialMediaQueryMatch)

  const {
    COOKIES: {HAS_SEEN_SAVE_NEWS_COACHMARK},
    COOKIE_EXPIRATIONS
  } = domainConfig.get('COMMON')
  const [isSaveNewsCoachmarkDismissed, setIsSaveNewsCoachmarkDismissed] = useState(hasSeenSaveNewsCoachmark)

  const dismissSaveNewsCoachmark = () => {
    jsCookie.set(HAS_SEEN_SAVE_NEWS_COACHMARK, true, {expires: COOKIE_EXPIRATIONS.ONE_YEAR})
    setIsSaveNewsCoachmarkDismissed(true)
  }

  const saveNewsButton = isNewsBookmarksEnabled ? (
    <ActionSaveNews
      newsId={articleId}
      isSaved={isCurrentArticleSaved}
      pageName={pageName}
      pageType={newsDetailPageType}
      showLabel={isTabletOrHigher}
      size={atomButtonSizes.LARGE}
    />
  ) : undefined

  const saveNewsAction = saveNewsButton ? (
    <OnboardingCoachmark
      badge={i18n.t('SEARCH_SAVE_ONBOARDING_LABEL_NEW')}
      title={i18n.t('NEWS_BOOKMARKS.COACHMARK_TITLE')}
      text={i18n.t('NEWS_BOOKMARKS.COACHMARK_TEXT')}
      isEnabled={!isSaveNewsCoachmarkDismissed}
      onClose={dismissSaveNewsCoachmark}
    >
      {saveNewsButton}
    </OnboardingCoachmark>
  ) : undefined

  const pickMainFeedImage = images => {
    const {images: adaptedImages} = getImagesUrlsAdapted({
      images: [{src: images[0]}],
      options: {
        aspectRatio: imageUrlAdapterAspectRatios.WIDE_SCREEN,
        imageType: imageUrlAdapterImageTypes.CARD_S,
        transformType: imageUrlAdapterTransformTypes.DEVICE_CROP,
        imageWidth: 300
      }
    })

    return adaptedImages[0].src
  }

  const newsFeed = {
    items: relatedContent.map(({comments, description, id, images, link, tag, title}) => ({
      articleId: id,
      comments,
      description,
      id,
      img: pickMainFeedImage(images),
      tag,
      title,
      url: link
    }))
  }

  const headerImage = getImagesUrlsAdapted({
    images: [{src: mainImage}],
    options: {
      aspectRatio: imageUrlAdapterAspectRatios.WIDE_SCREEN,
      imageType: isTabletOrHigher
        ? imageUrlAdapterImageTypes.ARTICLE_BODY
        : imageUrlAdapterImageTypes.MOBILE_ARTICLE_BODY,
      transformType: imageUrlAdapterTransformTypes.DEVICE_CROP,
      imageWidth: ARTICLE_BODY_IMAGE_WIDTH
    }
  }).images[0].src

  const hasImages = Boolean(images.length)
  const shouldRenderBreadcrumbs = Boolean(breadcrumbs.length) && !isWebview
  const shouldRenderGallery = hasImages && !isWebview
  const shouldRenderRelatedAds = !isWebview

  const getCommonPageTrackingParams = () => {
    const [firstVehicle] = vehicles
    const hasMake = Boolean(firstVehicle && firstVehicle.make)
    const hasModel = Boolean(firstVehicle && firstVehicle.model)

    return {
      pageName: title,
      tag: category,
      url: articleSlug,
      ...(hasMake && {
        make: firstVehicle.make.name,
        makeId: firstVehicle.make.id
      }),
      ...(hasModel && {
        model: firstVehicle.model.name,
        modelId: firstVehicle.model.id
      })
    }
  }

  const trackPageViewed = async () => {
    const pageTrackingData = await domain.get('get_page_tracking_data_use_case').execute({
      page: pageName,
      origin,
      ...getCommonPageTrackingParams()
    })

    analyticsTrackPage(pageTrackingData)
  }

  useMount(() => {
    // Restore scroll after scrolling to botton in the previous page
    window.scrollTo(0, 0)

    if (redirectTo || isWebview) return

    trackPageViewed()
  })

  useEffect(() => {
    if (redirectTo) push(redirectTo)
  }, [redirectTo, push])

  if (redirectTo) {
    return <></>
  }

  const pageTitle = isNewsCommentsPage ? i18n.t('COMMENTS.PAGE.TITLE', {title}) : title
  const showNumberOfComments = allowComments && pageName !== NEWS_COMMENTS_PAGE_NAME

  const shouldRenderGalleryDefaultPosition = shouldRenderGallery && !isNewsCommentsPage
  const shouldRenderGalleryBottomPosition = shouldRenderGallery && isNewsCommentsPage

  const _handleScrollToBestOffers = () => {
    const elementContactForm = document.getElementById('sidebar-id')
    elementContactForm && elementContactForm.scrollIntoView(scrollParams)
  }
  const _handleSidebarOnScreen = isOnScreen => {
    setIsSidebarOnScreen(isOnScreen)
  }

  const getConfiguratorUrl = () => {
    if (!make?.name || !model?.name) return '#'
    const makeSlug = transformToKebabCase(make?.name)
    const modelSlug = transformToKebabCase(model?.name)
    return `${VEHICLE_CONFIGURATOR_PATH}${makeSlug}/${modelSlug}/`
  }

  const makeModel = `${make?.name || ''} ${model?.name || ''}`.trim()
  const configuratorUrl = getConfiguratorUrl()
  return (
    <>
      <SharedSmartbanner
        pageName={pageName}
        position={sharedSmartbannerPositions.STICKY}
        hasTopbarOffset={true}
        source={SMARTBANNER_SOURCE_IDS.EDITORIAL}
        trackingOrigin={hitOriginSmartbannerNewsDetail}
        trackingInformation={hitInformationSmartbannerPositionTop}
      />
      <Layout.Basic isPaddingless linkFactory={LinkFactory}>
        <div className={NEWS_DETAIL_BASE_CLASS}>
          <div className={`${NEWS_DETAIL_BASE_CLASS}-content`}>
            {shouldRenderBreadcrumbs && (
              <div className={`${NEWS_DETAIL_BASE_CLASS}-contentBreadcrumbs`}>
                <Breadcrumb items={breadcrumbs} linkFactory={LinkFactory} />
              </div>
            )}
            <div ref={headerRef}>
              <PanelNewsDetailHeader
                actionSlot={saveNewsAction}
                author={author}
                category={category}
                tag={tag}
                date={date}
                linkFactory={LinkFactory}
                mainImageSrc={headerImage}
                slug={articleSlug}
                title={pageTitle}
                totalNumberOfComments={totalNumberOfComments}
                showNumberOfComments={showNumberOfComments}
              />
            </div>

            {isNewsCommentsPage ? (
              <div className={`${NEWS_DETAIL_BASE_CLASS}-backToArticle`}>
                <AtomButton
                  size={atomButtonSizes.LARGE}
                  design={atomButtonDesigns.SOLID}
                  shape={atomButtonShapes.CIRCULAR}
                  link
                  href={articleSlug}
                  linkFactory={LinkFactory}
                >
                  {i18n.t('COMMENTS.PAGE.GO_TO_NEWS_BUTTON')}
                </AtomButton>
              </div>
            ) : (
              <ArticleBody
                content={body}
                customContentConfig={customContentConfig}
                hasBodyNewsletter={hasBodyNewsletter}
                pageName={pageName}
                professionalSellerAds={professionalSellerAds}
                professionalSellerAdsListingUrl={professionalSellerAdsListingUrl}
                vehicles={vehicles}
              />
            )}
            <div className={NEWS_DETAIL_EXTRA_INFO_BASE_CLASS}>
              {faqsForUrlItems.length > 0 && (
                <section className={`${NEWS_DETAIL_BASE_CLASS}-faqsForUrl`}>
                  <TitleBasic as={titleBasicTags.h3} title={i18n.t('COMMON.FAQ_TITLE')} size={titleBasicSizes.s} />
                  <PanelFaqs questions={faqsForUrlItems} withFaqsStructuredData={false} />
                </section>
              )}
              <ProsAndConsSection prosAndCons={prosAndCons} layout={isTabletOrHigher ? 'horizontal' : 'vertical'} />
              {shouldRenderGalleryDefaultPosition && (
                <ImageGallerySection images={images} newsTitle={title} aspectRatio={GALLERY_ASPECT_RATIO.wideScreen} />
              )}
              <CommentsAndReviewsSection
                title={title}
                allowComments={allowComments}
                comments={comments}
                articleId={articleId}
                hideSectionTitle={isNewsCommentsPage}
              />

              {shouldRenderGalleryBottomPosition && (
                <ImageGallerySection images={images} newsTitle={title} aspectRatio={GALLERY_ASPECT_RATIO.wideScreen} />
              )}
              {shouldRenderRelatedAds && (
                <RelatedAdsSection
                  adsNew={adsNew}
                  adsRenting={adsRenting}
                  make={make?.name}
                  model={model?.name}
                  relatedAdsGroups={relatedAds}
                />
              )}
              {shouldRenderRelatedContent && (
                <SectionBasic title={i18n.t('COMMON.RELATED_NEWS')}>
                  <FeedArticles
                    initialNewsSearchFeed={newsFeed}
                    linkFactory={LinkFactory}
                    renderCardActions={
                      isNewsBookmarksEnabled
                        ? item => (
                            <ActionSaveNews
                              newsId={item.articleId}
                              isSaved={savedIdsSet.has(item.articleId)}
                              pageName={pageName}
                              pageType={newsDetailPageType}
                            />
                          )
                        : undefined
                    }
                  />
                </SectionBasic>
              )}
            </div>
          </div>
          <div className={`${NEWS_DETAIL_BASE_CLASS}-sidebar-container`} id="sidebar-id">
            <Sidebar onScreen={_handleSidebarOnScreen}>
              <Sidebar.NewsletterForm isShown={NEWSLETTER_SIDEBAR_ENABLED && !isWebview} pageName={pageName} />
              <Sidebar.Banner isShown={!isMobile && showConfigurator}>
                <BannerBasic isMarginless>
                  <BannerBasic.Title>{i18n.t('VEHICLE_CONFIGURATOR.ACCESS_BANNER.TITLE')}</BannerBasic.Title>
                  <BannerBasic.Description>
                    {i18n.t('VEHICLE_CONFIGURATOR.ACCESS_BANNER.DESCRIPTION', {makeModel})}
                  </BannerBasic.Description>
                  <BannerBasic.Button href={configuratorUrl}>{i18n.t('COMMON.CONFIGURE')}</BannerBasic.Button>
                </BannerBasic>
              </Sidebar.Banner>
              <Sidebar.BestOffers
                isShown={hasBestOffers}
                bestOffers={bestOffers}
                title={isMobile && i18n.t('COMMON_RECOMMENDED_PLURAL')}
                showLogo={isMobile}
              />
              <Sidebar.RelatedContent
                isShown={shouldRenderRelatedContentAtSidebar}
                title={i18n.t('COMMON.RELATED_NEWS')}
                newsFeed={newsFeed}
                renderCardActions={
                  isNewsBookmarksEnabled
                    ? item => (
                        <ActionSaveNews
                          newsId={item.articleId}
                          isSaved={savedIdsSet.has(item.articleId)}
                          pageName={pageName}
                          pageType={newsDetailPageType}
                        />
                      )
                    : undefined
                }
              />
            </Sidebar>
          </div>
          {hasBestOffers && !showConfigurator && (
            <ButtonSticky
              fullWidth
              hide={isSidebarOnScreen || isDescriptionOnScreen}
              onClick={_handleScrollToBestOffers}
            >
              {i18n.t('COMMON_RECOMMENDED_OFFERS')}
            </ButtonSticky>
          )}
          {showConfigurator && (
            <ButtonSticky
              fullWidth
              href={configuratorUrl}
              title={i18n.t('COMMON.CONFIGURE_YOUR_MAKE_MODEL', {makeModel})}
              link
            >
              {i18n.t('COMMON.CONFIGURE')}
            </ButtonSticky>
          )}
        </div>
        {!isWebview && <MotorTagCloud initialTagclouds={tagClouds} linkFactory={LinkFactory} pageName={pageName} />}
      </Layout.Basic>
      <Helmet {...helmet.head} />
      {helmet.structuredDataScripts?.map((structuredData, index) => (
        <ScriptJsonLD json={structuredData} key={index} />
      ))}
      {loadThirdParties && (
        <>
          <AdDisplay pageName={pageName} category={category} make={make} model={model} />
        </>
      )}
    </>
  )
}

NewsDetail.displayName = 'NewsDetail'

NewsDetail.propTypes = {
  __HTTP__: PropTypes.object,
  adsNew: PropTypes.object,
  adsRenting: PropTypes.object,
  allowComments: PropTypes.bool,
  articleId: PropTypes.string,
  articleSlug: PropTypes.string,
  author: PropTypes.object,
  bestOffers: PropTypes.array,
  body: PropTypes.array,
  breadcrumbs: PropTypes.array,
  category: PropTypes.string,
  comments: PropTypes.array,
  isLogged: PropTypes.bool,
  customContentConfig: PropTypes.object,
  date: PropTypes.string,
  experiments: PropTypes.object,
  faqsForUrlItems: PropTypes.array,
  hasSeenSaveNewsCoachmark: PropTypes.bool,
  helmet: PropTypes.object,
  images: PropTypes.array,
  isCurrentArticleSaved: PropTypes.bool,
  isNewsBookmarksEnabled: PropTypes.bool,
  isNewsCommentsPage: PropTypes.bool,
  loadThirdParties: PropTypes.bool,
  make: PropTypes.shape({id: PropTypes.string, name: PropTypes.string}),
  model: PropTypes.shape({id: PropTypes.string, name: PropTypes.string}),
  origin: PropTypes.string,
  pageName: PropTypes.string,
  prosAndCons: PropTypes.array,
  professionalSellerAds: PropTypes.array,
  professionalSellerAdsListingUrl: PropTypes.string,
  rating: PropTypes.object,
  relatedAds: PropTypes.array,
  relatedContent: PropTypes.array,
  savedArticleIds: PropTypes.array,
  showConfigurator: PropTypes.bool,
  tagClouds: PropTypes.array,
  tag: PropTypes.string,
  title: PropTypes.string,
  totalNumberOfComments: PropTypes.number,
  vehicles: PropTypes.array
}

NewsDetail.getInitialProps = getInitialProps
NewsDetail.renderLoading = () => <Skeleton />

export default NewsDetail
